import * as R from 'ramda';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../../core/api/module';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { ActionType } from '../../../../core/redux/actionType';
import { DevItemComment } from '../models';


const KEY_ACTION = ActionType.AddDevItemCommentRequest;
const ACTIONS    = [
  ActionType.AddDevItemCommentRequest,
  ActionType.AddDevItemCommentResponse,
  ActionType.AddDevItemCommentError,
];


type Payload = DevItemComment;


type Meta = {
  projectId: number;
  devItemId: number;
  devActionId: number;
  email: string;
};


/**
 * Dispatch this to associate a new comment with a dev item. Also associates the added comment to the commenting
 * collaborator.
 *
 * @param projectId - the database Id for the project containing the commented dev item
 * @param participantId - the database Id for the participant to whose dev item we're adding a comment
 * @param devItemId - the database Id for the commented dev item
 * @param devActionId - the database Id for the specific action in the dev item to which the comment is to be added
 * @param email - the email address of the commenting user
 * @param comment - the content of the posted comment
 */
export const addDevItemCommentAction = (
  projectId: number,
  participantId: number,
  devItemId: number,
  devActionId: number,
  email: string,
  comment: string,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.AddDevItemComment,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        projectId:     projectId,
        participantId: participantId,
        devItemId:     devItemId,
        devActionId:   devActionId,
        email:         email,
        comment:       comment,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId:   projectId,
        devItemId:   devItemId,
        devActionId: devActionId,
        email:       email,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${devItemId}-${devActionId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId, devItemId, devActionId } = action.meta;

  const metaTag = `${projectId}-${devItemId}-${devActionId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  if (!payload) {
    return successState;
  }

  let itemComments = R.path<DevItemComment[]>(
    ['devPlan', 'comments', projectId, `${devItemId}`],
    successState,
  );

  itemComments = payload ? (itemComments ?? []).concat(payload) : itemComments;

  return R.assocPath(
    ['devPlan', 'comments', projectId, `${devItemId}`],
    itemComments,
    successState,
  );
};

export const addDevItemCommentReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
